import { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { cn } from '../utils/cn';

interface CacheStatusIndicatorProps {
  /** Timestamp (ms) when the cache was last populated. null if never fetched. */
  cachedAt: number | null;
  /** Callback to trigger a force-refresh of the data. */
  onRefresh: () => Promise<void>;
  /** Cache TTL in milliseconds. Defaults to 30 minutes. */
  ttl?: number;
}

export const CacheStatusIndicator = ({ cachedAt, onRefresh, ttl = 30 * 60 * 1000 }: CacheStatusIndicatorProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [, setTick] = useState(0);

  // Re-render every 30s to keep the "cached X ago" label fresh
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefresh]);

  if (cachedAt === null) {
    return null;
  }

  const ageMs = Date.now() - cachedAt;
  const ageMinutes = Math.floor(ageMs / 60_000);
  const nearingExpiry = ageMs > ttl * 0.66; // >20m for a 30m TTL
  const stale = ageMs > ttl;

  let ageLabel: string;
  if (ageMinutes < 1) {
    ageLabel = 'just now';
  } else if (ageMinutes === 1) {
    ageLabel = '1m ago';
  } else {
    ageLabel = `${ageMinutes}m ago`;
  }

  return (
    <div className="inline-flex items-center gap-1">
      <div className="inline-flex items-center gap-1.5 text-xs text-glass-400 tabular-nums whitespace-nowrap">
        <div
          className={cn(
            'w-1.5 h-1.5 rounded-full flex-shrink-0',
            stale
              ? 'bg-red-400'
              : nearingExpiry
                ? 'bg-yellow-400'
                : 'bg-emerald-400'
          )}
        />
        <span>Cached {ageLabel}</span>
      </div>
      <button
        onClick={handleRefresh}
        disabled={isRefreshing}
        className="btn-icon btn-icon-sm"
        title="Refresh data from Redis"
        aria-label="Refresh data from Redis"
      >
        <RefreshCw className={cn('w-3.5 h-3.5', isRefreshing && 'animate-spin')} />
      </button>
    </div>
  );
};
